"use client";
import { useState, useEffect, useRef, ChangeEvent, FormEvent } from "react";
import {
  User,
  Edit2,
  Eye,
  EyeOff,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import api from "../../services/api";
import { useToast } from "../../hooks/useToast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import DatePicker, { ReactDatePickerCustomHeaderProps } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/datepicker-custom.css";

const DOB_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function useIsCompactScreen(maxWidth = 768) {
  const [isCompact, setIsCompact] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(`(max-width: ${maxWidth}px)`).matches : false
  );

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = () => setIsCompact(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [maxWidth]);

  return isCompact;
}

function DobPickerHeader({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: ReactDatePickerCustomHeaderProps) {
  const [openMenu, setOpenMenu] = useState<"month" | "year" | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, []);

  return (
    <div ref={menuRef} className="relative z-20 bg-[#885ABB] rounded-t-[11px]">
      <div className="flex items-center justify-between gap-1 px-2 sm:px-2.5 py-2">
        <button
          type="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          className="p-2 sm:p-1.5 text-white rounded-lg hover:bg-white/20 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setOpenMenu((v) => (v === "month" ? null : "month"))}
            className="inline-flex items-center gap-0.5 rounded-md bg-white/15 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white hover:bg-white/25 transition-colors"
            aria-label="Select month"
          >
            {DOB_MONTHS[date.getMonth()]}
            <ChevronDown size={12} className="opacity-80" />
          </button>
          <button
            type="button"
            onClick={() => setOpenMenu((v) => (v === "year" ? null : "year"))}
            className="inline-flex items-center gap-0.5 rounded-md bg-white/15 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white hover:bg-white/25 transition-colors"
            aria-label="Select year"
          >
            {date.getFullYear()}
            <ChevronDown size={12} className="opacity-80" />
          </button>
        </div>

        <button
          type="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          className="p-2 sm:p-1.5 text-white rounded-lg hover:bg-white/20 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
          aria-label="Next month"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {openMenu === "month" && (
        <div className="absolute left-1.5 right-1.5 sm:left-2 sm:right-2 top-[calc(100%-2px)] z-30 rounded-lg border border-purple-100 bg-white p-2 shadow-lg">
          <div className="grid grid-cols-3 gap-1">
            {DOB_MONTHS.map((month, index) => {
              const selected = index === date.getMonth();
              return (
                <button
                  key={month}
                  type="button"
                  onClick={() => {
                    changeMonth(index);
                    setOpenMenu(null);
                  }}
                  className={`rounded-md px-1.5 py-2 sm:py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                    selected
                      ? "bg-[#885ABB] text-white"
                      : "text-[#402B69] hover:bg-[#F3E8FF] active:bg-[#F3E8FF]"
                  }`}
                >
                  {month}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {openMenu === "year" && (
        <div className="absolute left-1.5 right-1.5 sm:left-2 sm:right-2 top-[calc(100%-2px)] z-30 max-h-40 sm:max-h-44 overflow-y-auto overscroll-contain rounded-lg border border-purple-100 bg-white p-1.5 shadow-lg">
          <div className="grid grid-cols-4 gap-1">
            {years.map((year) => {
              const selected = year === date.getFullYear();
              return (
                <button
                  key={year}
                  type="button"
                  onClick={() => {
                    changeYear(year);
                    setOpenMenu(null);
                  }}
                  className={`rounded-md px-1 py-2 sm:py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                    selected
                      ? "bg-[#885ABB] text-white"
                      : "text-[#402B69] hover:bg-[#F3E8FF] active:bg-[#F3E8FF]"
                  }`}
                >
                  {year}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

type CustomerForm = {
  name: string;
  email: string;
  mobile: string;
  date_of_birth: string;
  image: File | null;
  calling_code: string;
  landline: string;
  skin_type: string;
  password?: string;
  pin_code?: string;
};

const SKIN_TYPES = ["fair", "sensitive", "medium", "dark"] as const;

interface AddCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  editData?: any;
}

function parseLocalDate(value: string): Date | null {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function AddCustomerModal({
  isOpen,
  onClose,
  onSave,
  editData,
}: AddCustomerModalProps) {
  const toast = useToast();
  const isCompactScreen = useIsCompactScreen(768);
  const [form, setForm] = useState<CustomerForm>({
    name: "",
    email: "",
    mobile: "",
    date_of_birth: "",
    image: null,
    calling_code: "+44",
    landline: "",
    skin_type: "",
    password: "",
    pin_code: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showPinCode, setShowPinCode] = useState(false);

  useEffect(() => {
    if (editData) {
      setForm({
        name: editData.name || "",
        email: editData.email || "",
        mobile: editData.mobile || "",
        date_of_birth: editData.date_of_birth || "",
        image: null,
        calling_code: editData.calling_code || "+44",
        landline: editData.landline || "",
        skin_type: (editData.skin_type || "").toLowerCase(),
        password: "",
        pin_code: "",
      });
      if (editData.user_image_url) {
        setImagePreview(editData.user_image_url);
      }
    } else {
      // Reset form when modal opens for adding new customer
      setForm({
        name: "",
        email: "",
        mobile: "",
        date_of_birth: "",
        image: null,
        calling_code: "+44",
        landline: "",
        skin_type: "",
        password: "",
        pin_code: "",
      });
      setImagePreview(null);
    }
    setError(null);
    setFieldErrors({});
    setShowPinCode(false);
  }, [editData, isOpen]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear field-specific error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    if (error) setError(null);
  };

  const handlePinChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 8);
    setForm((prev) => ({ ...prev, pin_code: value }));
    if (fieldErrors.pin_code) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.pin_code;
        return newErrors;
      });
    }
    if (error) setError(null);
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm((prev) => ({ ...prev, image: file }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string[]> = {};

    if (!form.name.trim()) {
      newErrors.name = ["Client name is required"];
    } else if (form.name.trim().length < 2) {
      newErrors.name = ["Name must be at least 2 characters long"];
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim()) {
      newErrors.email = ["Email address is required"];
    } else if (!emailRegex.test(form.email)) {
      newErrors.email = ["Please enter a valid email address (e.g., name@example.com)"];
    }

    if (!form.mobile.trim()) {
      newErrors.mobile = ["Mobile number is required"];
    } else if (form.mobile.trim().length < 7) {
      newErrors.mobile = ["Mobile number is too short"];
    }

    if (!editData && !form.password) {
      newErrors.password = ["Password is required for new clients"];
    } else if (form.password && form.password.length < 6) {
      newErrors.password = ["Password must be at least 6 characters long"];
    }

    if (form.pin_code && (form.pin_code.length < 1 || form.pin_code.length > 8)) {
      newErrors.pin_code = ["PIN must be 1–8 digits"];
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setError(null);
    setFieldErrors({});

    if (!validateForm()) {
      toast.error("Please correct the errors in the form");
      return;
    }

    setLoading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("name", form.name.trim());
      formData.append("email", form.email.trim());
      formData.append("mobile", form.mobile.trim());
      formData.append("calling_code", form.calling_code || "+44");

      if (form.date_of_birth) {
        formData.append("date_of_birth", form.date_of_birth);
      }

      if (form.landline) {
        formData.append("landline", form.landline);
      }

      if (form.skin_type) {
        formData.append("skin_type", form.skin_type);
      }

      if (form.password) {
        formData.append("password", form.password);
      }

      if (form.pin_code) {
        formData.append("pin_code", form.pin_code);
      }

      if (form.image) {
        formData.append("image", form.image);
      }

      let response;

      if (editData) {
        // Laravel apiResource update = PUT /admin/customers/{id}
        // Use POST + _method=PUT so multipart/file uploads parse correctly
        formData.append("_method", "PUT");
        // Do NOT set Content-Type manually — axios must add the multipart boundary
        response = await api.post(`/admin/customers/${editData.id}`, formData);
      } else {
        response = await api.post("/admin/customers", formData);
      }

      if (response.data.success) {
        toast.success(editData ? "Customer updated successfully" : "Customer added successfully");
        const apiCustomer = response.data.data?.customer || response.data.data || {};
        onSave({
          ...(editData || {}),
          ...apiCustomer,
          id: apiCustomer.id || editData?.id,
          name: form.name || apiCustomer.name,
          email: form.email || apiCustomer.email,
          mobile: form.mobile || apiCustomer.mobile,
          date_of_birth: form.date_of_birth || apiCustomer.date_of_birth,
          calling_code: form.calling_code || apiCustomer.calling_code,
          landline: form.landline || apiCustomer.landline,
          skin_type: form.skin_type || apiCustomer.skin_type || null,
          user_image_url:
            apiCustomer.user_image_url ||
            apiCustomer.image ||
            imagePreview ||
            editData?.user_image_url,
          image:
            apiCustomer.image ||
            apiCustomer.user_image_url ||
            imagePreview ||
            editData?.image,
        });
        onClose();
        // Reset form
        setForm({
          name: "",
          email: "",
          mobile: "",
          date_of_birth: "",
          image: null,
          calling_code: "+44",
          landline: "",
          skin_type: "",
          password: "",
          pin_code: "",
        });
        setImagePreview(null);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to save customer. Please try again.";
      setError(errorMessage);

      const errors = err.response?.data?.errors;
      if (errors) {
        setFieldErrors(errors);
        // Toast the first error message for immediate feedback
        const firstErrorKey = Object.keys(errors)[0];
        if (firstErrorKey && errors[firstErrorKey][0]) {
          toast.error(errors[firstErrorKey][0]);
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error(errorMessage);
      }

      console.error("Error saving customer:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedDob = parseLocalDate(form.date_of_birth);

  return (
    <div className="fixed inset-0 bg-white z-[1000] flex flex-col">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center border-b px-6 md:px-12 py-5 md:py-6 gap-4">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">
          {editData ? "Edit client" : "Add a new client"}
        </h2>
        <div className="flex gap-3 md:gap-4">
          <button
            onClick={onClose}
            type="button"
            disabled={loading}
            className="px-4 md:px-5 py-2 border border-gray-300 rounded-lg text-sm md:text-base font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Close
          </button>
          <button
            type="submit"
            form="client-form"
            disabled={loading}
            className="px-4 md:px-5 py-2 bg-[#885ABB] text-white rounded-lg text-sm md:text-base font-medium hover:bg-[#6f47a2] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden flex-col md:flex-row">
        {/* Sidebar */}
        <aside className="w-full md:w-64 border-b md:border-r p-4 md:p-6 text-sm md:text-base">
          <nav className="space-y-4 md:space-y-6">
            <div>
              <p className="font-semibold mb-2 md:mb-3 text-gray-900">Personal</p>
              <ul className="flex md:flex-col gap-2 md:gap-0">
                <li className="bg-purple-50 text-[#885ABB] border border-purple-200 rounded-lg px-3 py-2 font-medium text-sm">
                  Profile
                </li>
              </ul>
            </div>
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 p-6 md:p-10 overflow-y-auto">
          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
            Profile
          </h3>
          <p className="text-gray-600 text-sm md:text-base mb-6 md:mb-8">
            Manage your client&apos;s personal profile
          </p>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Avatar */}
          <div className="flex items-center gap-4 md:gap-5 mb-6 md:mb-8">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-purple-100 flex items-center justify-center relative border border-purple-200 overflow-hidden">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Customer"
                  className="w-full h-full object-cover"
                />
              ) : editData?.user_image_url ? (
                <img
                  src={editData.user_image_url}
                  alt="Customer"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-10 h-10 md:w-12 md:h-12 text-purple-700" />
              )}
              <label
                htmlFor="image-upload"
                className="absolute bottom-0 right-0 bg-white border border-gray-300 rounded-full p-1.5 shadow-sm hover:bg-gray-50 cursor-pointer"
              >
                <Edit2 className="w-3 h-3 md:h-4 text-gray-700" />
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Form */}
          <form
            id="client-form"
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-full md:max-w-5xl"
          >
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. John Doe"
                className={`w-full border rounded-lg p-2.5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.name ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
                required
              />
              {fieldErrors.name && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.name[0]}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="example@domain.com"
                className={`w-full border rounded-lg p-2.5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
                required
              />
              {fieldErrors.email && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.email[0]}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Mobile <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <Select
                  value={form.calling_code}
                  onValueChange={(value) => setForm(prev => ({ ...prev, calling_code: value }))}
                >
                  <SelectTrigger className="w-[100px] border border-gray-300 rounded-lg h-[45px] focus:ring-purple-500">
                    <SelectValue placeholder="+44" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[1100]">
                    <SelectItem value="+44">+44</SelectItem>
                    <SelectItem value="+1">+1</SelectItem>
                    <SelectItem value="+353">+3</SelectItem>
                    <SelectItem value="+61">+61</SelectItem>
                    <SelectItem value="+91">+9</SelectItem>
                    <SelectItem value="+92">+92</SelectItem>
                  </SelectContent>
                </Select>
                <input
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  placeholder="e.g. 7123 456789"
                  className={`flex-1 border rounded-lg p-2.5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.mobile ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
                  required
                />
              </div>
              {fieldErrors.mobile && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.mobile[0]}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Skin type
              </label>
              <Select
                value={form.skin_type || undefined}
                onValueChange={(value) =>
                  setForm((prev) => ({ ...prev, skin_type: value }))
                }
              >
                <SelectTrigger className="w-full border border-gray-300 rounded-lg h-[45px] focus:ring-purple-500">
                  <SelectValue placeholder="Select skin type" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[1100]">
                  {SKIN_TYPES.map((type) => (
                    <SelectItem key={type} value={type} className="capitalize">
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Date of Birth
              </label>
              <style>{`
                .customer-dob-datepicker.react-datepicker {
                  display: block !important;
                  flex-direction: column !important;
                  width: min(300px, calc(100vw - 2rem)) !important;
                  max-width: calc(100vw - 2rem) !important;
                  border: 1px solid #E9D5FF !important;
                  border-radius: 12px !important;
                  overflow: visible !important;
                  font-family: inherit;
                  box-shadow: 0 12px 28px rgba(64, 43, 105, 0.14);
                }
                .customer-dob-datepicker .react-datepicker__month-container {
                  width: 100% !important;
                  float: none !important;
                  overflow: visible !important;
                }
                .customer-dob-datepicker .react-datepicker__header {
                  background: #885ABB !important;
                  border-bottom: none !important;
                  border-top-left-radius: 11px !important;
                  border-top-right-radius: 11px !important;
                  padding: 0 !important;
                  overflow: visible !important;
                  position: relative;
                  z-index: 5;
                }
                .customer-dob-datepicker .react-datepicker__header__dropdown,
                .customer-dob-datepicker .react-datepicker__current-month {
                  display: none !important;
                }
                .customer-dob-datepicker .react-datepicker__day-names {
                  display: flex;
                  justify-content: space-between;
                  margin: 0;
                  padding: 6px 8px 2px;
                  background: #fff;
                }
                .customer-dob-datepicker .react-datepicker__day-name {
                  flex: 1 1 0;
                  width: auto !important;
                  line-height: 1.75rem !important;
                  margin: 0 !important;
                  color: #9CA3AF !important;
                  font-size: 0.7rem !important;
                  font-weight: 600 !important;
                }
                .customer-dob-datepicker .react-datepicker__month {
                  margin: 0.35rem 0.4rem 0.55rem !important;
                }
                .customer-dob-datepicker .react-datepicker__week {
                  display: flex;
                  justify-content: space-between;
                }
                .customer-dob-datepicker .react-datepicker__day {
                  flex: 1 1 0;
                  width: auto !important;
                  max-width: 2.4rem;
                  line-height: 2.15rem !important;
                  margin: 0.12rem 0 !important;
                  border-radius: 8px !important;
                  color: #402B69 !important;
                  font-size: 0.8rem !important;
                  font-weight: 500 !important;
                }
                .customer-dob-datepicker .react-datepicker__day:hover {
                  background: #F3E8FF !important;
                  color: #402B69 !important;
                }
                .customer-dob-datepicker .react-datepicker__day--selected,
                .customer-dob-datepicker .react-datepicker__day--keyboard-selected {
                  background: #885ABB !important;
                  color: #fff !important;
                  font-weight: 600 !important;
                }
                .customer-dob-datepicker .react-datepicker__day--outside-month {
                  color: #D1D5DB !important;
                }
                .customer-dob-datepicker .react-datepicker__day--disabled {
                  color: #E5E7EB !important;
                  cursor: not-allowed;
                }
                .customer-dob-datepicker .react-datepicker__navigation {
                  display: none !important;
                }
                .customer-dob-popper {
                  z-index: 1100 !important;
                  max-width: calc(100vw - 1rem);
                }
                .customer-dob-popper .react-datepicker__triangle {
                  display: none !important;
                }
                .react-datepicker__portal {
                  background-color: rgba(64, 43, 105, 0.35) !important;
                  z-index: 1200 !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                  padding: 1rem !important;
                }
                .react-datepicker__portal .customer-dob-datepicker.react-datepicker {
                  width: min(320px, calc(100vw - 2rem)) !important;
                }
                @media (max-width: 480px) {
                  .customer-dob-datepicker .react-datepicker__day {
                    line-height: 2.35rem !important;
                    font-size: 0.85rem !important;
                  }
                  .customer-dob-datepicker .react-datepicker__day-name {
                    font-size: 0.65rem !important;
                  }
                }
              `}</style>
              <div className="relative">
                <DatePicker
                  selected={selectedDob}
                  onChange={(date: Date | null) => {
                    setForm((prev) => ({
                      ...prev,
                      date_of_birth: date ? formatLocalDate(date) : "",
                    }));
                    if (fieldErrors.date_of_birth) {
                      setFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.date_of_birth;
                        return next;
                      });
                    }
                  }}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Select date"
                  maxDate={new Date()}
                  withPortal={isCompactScreen}
                  portalId="root"
                  popperPlacement="bottom-start"
                  popperProps={{ strategy: "fixed" }}
                  popperClassName="customer-dob-popper"
                  calendarClassName="customer-dob-datepicker"
                  renderCustomHeader={(props) => <DobPickerHeader {...props} />}
                  className={`w-full border rounded-lg p-2.5 pr-10 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.date_of_birth ? "border-red-500 bg-red-50" : "border-gray-300 hover:border-purple-300"}`}
                  wrapperClassName="w-full"
                />
                <CalendarIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#885ABB]" />
              </div>
              {fieldErrors.date_of_birth && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.date_of_birth[0]}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Landline
              </label>
              <input
                name="landline"
                value={form.landline}
                onChange={handleChange}
                placeholder="231231231"
                className={`w-full border rounded-lg p-2.5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.landline ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
              />
              {fieldErrors.landline && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.landline[0]}
                </p>
              )}
            </div>

            <div className="">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Password {!editData && <span className="text-red-500">*</span>}
                {editData && <span className="text-gray-500 text-xs ml-2">(Leave blank to keep current password)</span>}
              </label>
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                placeholder={editData ? "Enter new password (optional)" : "Enter password"}
                className={`w-full border rounded-lg p-2.5 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.password ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
                required={!editData}
              />
              {fieldErrors.password && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.password[0]}
                </p>
              )}
            </div>

            <div className="">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                PIN
                {editData && (
                  <span className="text-gray-500 text-xs ml-2">
                    (Leave blank to keep current PIN)
                  </span>
                )}
              </label>
              <div className="relative">
                <input
                  name="pin_code"
                  type={showPinCode ? "text" : "password"}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.pin_code}
                  onChange={handlePinChange}
                  placeholder={editData ? "Enter new PIN (optional)" : "Enter PIN (numbers only)"}
                  maxLength={8}
                  autoComplete="new-password"
                  className={`w-full border rounded-lg p-2.5 pr-10 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${fieldErrors.pin_code ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-purple-300'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPinCode((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#885ABB] transition-colors"
                  aria-label={showPinCode ? "Hide PIN" : "Show PIN"}
                >
                  {showPinCode ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {fieldErrors.pin_code && (
                <p className="text-red-500 text-xs mt-1 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full" /> {fieldErrors.pin_code[0]}
                </p>
              )}
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}
